import { ButtonHTMLAttributes, CSSProperties, FC, ReactNode } from 'react'
import classes from './index.module.css'
import cn from 'classnames'
import { LoadingOutlined } from '@ant-design/icons'

interface ButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'color'> {
  color?: 'primary' | 'secondary' | 'gray' | 'dark-gray' | 'white'
  variant?: 'default' | 'outline' | 'light'
  rightContent?: ReactNode
  leftContent?: ReactNode
  isLoading?: boolean
  classNames?: {
    button?: string
    loader?: string
  }
}

const Button: FC<ButtonProps> = ({
  color = 'primary',
  variant = 'default',
  rightContent,
  leftContent,
  children,
  className,
  isLoading,
  style,
  classNames,
  ...props
}) => {
  const palette = {
    primary: '#9747ff',
    text: '#ffffff',
  }

  let styleVar: Record<string, string> = {}

  switch (color) {
    case 'primary':
      palette.text = '#ffffff'
      palette.primary = '#9747ff'
      break

    case 'secondary':
      palette.text = '#000000'
      palette.primary = '#F9EDFF'
      break

    case 'gray':
      palette.text = '#000000'
      palette.primary = '#F4F2F2'
      break

    case 'dark-gray':
      palette.text = '#ffffff'
      palette.primary = '#A3A3A5'
      break

    case 'white':
      palette.text = '#000000'
      palette.primary = '#FFFFFF'
      break

    default:
      break
  }

  switch (variant) {
    case 'default':
      styleVar = {
        '--text-color': palette.text,
        '--background-color': palette.primary,
      }
      break

    case 'outline':
      styleVar = {
        '--text-color': palette.primary,
        '--background-color': 'transparent',
        '--border-color': palette.primary,
        '--border-type': 'solid',
        '--border-width': 'calc(0.0625 * var(--adapt-rem))', // 1px
      }
      break

    case 'light':
      styleVar = {
        '--text-color': palette.primary,
        '--background-color': `${palette.primary}2A`,
      }
      break
  }

  return (
    <button
      type="button"
      className={cn(classes.button, classNames?.button, className)}
      style={{ ...styleVar, ...style }}
      {...props}
    >
      <>
        {leftContent && <div>{leftContent}</div>}
        {isLoading && (
          <LoadingOutlined
            onPointerEnterCapture={undefined}
            onPointerLeaveCapture={undefined}
            className={cn(classes.loadingIcon, classNames?.loader)}
          />
        )}
        {children}
        {rightContent && <div>{rightContent}</div>}
      </>
    </button>
  )
}

export default Button
